import request from '@/utils/request'
import {
  buildAuditCsvFallbackName,
  parseContentDispositionFilename,
  triggerBlobDownload
} from '@/utils/posId'

/**
 * pos-id 治理模組 API 層（9 條路由，全部要求 auth:sanctum + abilities:server-admin）
 * 契約：docs/pos-id-frontend-guide-2026-10.md
 *
 * 兩個呼叫方標記（src/utils/request.js 已支援；未帶標記的既有請求行為不變）：
 *  - `skipErrorMessage`：不自動彈後端英文 message，錯誤一律由頁面用 resolvePosIdError 映射中文（guide §1.3）
 *  - `skipSuccessMessage`：不自動彈「操作成功！」，改由頁面展示後端 notes[]（guide §5 規則 2）
 *
 * 因此本模組的**每一支請求都帶 skipErrorMessage**（讀取類失敗時頁面都會顯示中文提示；
 * 401 仍由 interceptor 統一跳登入，不受此標記影響）。
 *
 * ⚠️ 寫入類注意：`PUT /api/film-circuit-special-ids/{id}` **不支援 dry_run**（後端會靜默忽略並真的寫入），
 * 因此 buildSpecialIdStatusPayload 刻意不產生 dry_run；停用 / 啟用走「本地確認 → 單次提交」。
 * 對照：`PUT /api/film-circuit-ids/{id}` 與 `POST /api/film-circuit-special-ids` 都支援 dry_run。
 */

const READ_CONFIG = {
  skipErrorMessage: true
}

const WRITE_CONFIG = {
  skipErrorMessage: true,
  skipSuccessMessage: true
}

// ── A. 匹配記錄 film_circuit_ids（無新增、無刪除）─────────────────

/** A1 GET /api/film-circuit-ids（列表 + 前綴搜尋） */
export function getFilmCircuitIds(params) {
  return request({
    url: '/api/film-circuit-ids',
    method: 'GET',
    params,
    ...READ_CONFIG
  })
}

/** A2 GET /api/film-circuit-ids/{id}（詳情；with_usage=0 可省掉用量摘要） */
export function getFilmCircuitId(id, params) {
  return request({
    url: `/api/film-circuit-ids/${id}`,
    method: 'GET',
    params,
    ...READ_CONFIG
  })
}

/** A3 PUT /api/film-circuit-ids/{id}（dry_run=true 時只算不寫，後端已驗證零寫入） */
export function updateFilmCircuitId(id, data) {
  return request({
    url: `/api/film-circuit-ids/${id}`,
    method: 'PUT',
    data,
    ...WRITE_CONFIG
  })
}

/** A4 GET /api/film-circuit-ids/{id}/audits（該筆記錄的變更歷史） */
export function getFilmCircuitIdAudits(id, params) {
  return request({
    url: `/api/film-circuit-ids/${id}/audits`,
    method: 'GET',
    params,
    ...READ_CONFIG
  })
}

// ── B. 白名單 film_circuit_special_ids（新增 / 停用 / 啟用，無刪除）──

/** B1 GET /api/film-circuit-special-ids */
export function getFilmCircuitSpecialIds(params) {
  return request({
    url: '/api/film-circuit-special-ids',
    method: 'GET',
    params,
    ...READ_CONFIG
  })
}

/** B2 POST /api/film-circuit-special-ids（backfill_ledger 預設 true；支援 dry_run 預演） */
export function createFilmCircuitSpecialId(data) {
  return request({
    url: '/api/film-circuit-special-ids',
    method: 'POST',
    data,
    ...WRITE_CONFIG
  })
}

/**
 * B3 PUT /api/film-circuit-special-ids/{id}（status='D' 停用 / '' 啟用）
 * ⚠️ 此端點沒有 dry_run：送了也會被忽略並且真的寫入（見檔頭說明）。
 */
export function updateFilmCircuitSpecialId(id, data) {
  return request({
    url: `/api/film-circuit-special-ids/${id}`,
    method: 'PUT',
    data,
    ...WRITE_CONFIG
  })
}

// ── C. 變更記錄 pos_id_audits ────────────────────────────────────

/** C1 GET /api/pos-id-audits */
export function getPosIdAudits(params) {
  return request({
    url: '/api/pos-id-audits',
    method: 'GET',
    params,
    ...READ_CONFIG
  })
}

/**
 * C2 GET /api/pos-id-audits/export（CSV + UTF-8 BOM，不分頁）
 * 不能用 window.open（不帶 Authorization 頭 → 401），必須 fetch/blob（guide §7.3-7）。
 * 回傳未解包的 axios response（需要 Content-Disposition 取檔名）。
 * 錯誤時 response.data 是 Blob：頁面要先讀出 JSON 再交給 resolvePosIdError，才拿得到 error.code。
 */
export function exportPosIdAudits(params) {
  return request({
    url: '/api/pos-id-audits/export',
    method: 'GET',
    params,
    responseType: 'blob',
    returnRawResponse: true,
    ...READ_CONFIG
  })
}

/**
 * C2 便利包裝：匯出並觸發瀏覽器下載。
 * @param {object} params 與列表相同的篩選參數（不含 page/count 也無妨，後端不分頁）
 * @param {object} filters 用於兜底檔名的原始篩選（date_start / date_end）
 * @returns {Promise<string>} 實際使用的檔名
 */
export function downloadPosIdAuditsCsv(params, filters) {
  return exportPosIdAudits(params).then((response) => {
    const headers = response && response.headers ? response.headers : {}
    const filename =
      parseContentDispositionFilename(headers['content-disposition']) ||
      buildAuditCsvFallbackName(filters)
    triggerBlobDownload(response.data, filename)
    return filename
  })
}

// ── D. 重定向映射 manual_fix_mappings（只讀 + 刪除，無新增 / 編輯）─────

/** D1 GET /api/manual-fix-mappings（列表 / 搜尋，含 to_record / from_ledger_exists / from_engagements 計算欄位） */
export function getManualFixMappings(params) {
  return request({
    url: '/api/manual-fix-mappings',
    method: 'GET',
    params,
    ...READ_CONFIG
  })
}

/** D2 GET /api/manual-fix-mappings/{id}（詳情 = 列表欄位 + samples[]，該舊號最近 5 條 engagement_finals） */
export function getManualFixMapping(id) {
  return request({
    url: `/api/manual-fix-mappings/${id}`,
    method: 'GET',
    ...READ_CONFIG
  })
}

/**
 * D3 DELETE /api/manual-fix-mappings/{id}
 * - `params` 傳 dry_run（guide §5.3 的 curl 用 ?dry_run=1）；`data` 傳 { reason }
 * - **物理刪除、無撤銷**；dry_run=1 時零寫入（audit_id=null、notes 用「將刪除…」）
 * - 刪除後對同一 id 再刪 → 10010；此路徑 POST/PUT/PATCH 一律 405
 */
export function deleteManualFixMapping(id, params, data) {
  return request({
    url: `/api/manual-fix-mappings/${id}`,
    method: 'DELETE',
    params,
    data,
    ...WRITE_CONFIG
  })
}
